import React from "react";

import { Col, Row } from "react-bootstrap";

import { pick } from "lodash";

import {
  Account,
  BankDetails,
  BusinessAndTax,
  Contact,
  FinanceContact,
  GetAccountDetailsByParticipantIdQuery,
  ParticipantTypes,
  Role,
  useGetAccountDetailsByParticipantIdQuery,
} from "@api/graphql";
import { MultiStepForm } from "@components/form/MultiStepForm";
import { useAuthenticationContext } from "@context/authentication";
import { DashboardHeader } from "@pages/dashboard/DashboardHeader";

import { ROUTES } from "../../routes";

import { useIsInternalUserCreatingAccount } from "./hooks/useIsInternalUserCreatingAccount";
import { steps } from "./steps";
import { UserDetailsInput, UserInput } from "./steps/RegisterFormTypes";

import styles from "./RegisterSubmission.module.scss";

const EMPTY_USER_INPUT: UserInput = {
  user_id: undefined,
  user_role: [],
  participant_type: [],
  user_details: {
    contact_details: {} as Contact,
    tax_details: {} as BusinessAndTax,
    account_details: {} as Account,
    bank_details: {} as BankDetails,
    finance_contact: {} as FinanceContact,
  } as UserDetailsInput,
};

const removeKeysWhoseValuesAreNull = (obj: object) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null));
const isNonEmptyString = (value: string | undefined) => value?.trim() !== "";

const getFormDataForPrecreatedUser = (data: GetAccountDetailsByParticipantIdQuery): UserInput => {
  const { getAccountDetailsByParticipantId: participant } = data;

  const relatedUser = participant.related_users?.[0] || null;
  const contact = participant.contact;

  const contact_details = {
    email: isNonEmptyString(contact?.email) ? contact?.email : relatedUser?.email,
    first_name: isNonEmptyString(contact?.first_name) ? contact?.first_name : relatedUser?.first_name,
    home_number: contact?.home_number,
    last_name: isNonEmptyString(contact?.last_name) ? contact?.last_name : relatedUser?.last_name,
    middle_name: isNonEmptyString(contact?.middle_name) ? contact?.middle_name : relatedUser?.middle_name,
    phone_number: isNonEmptyString(contact?.phone_number) ? contact?.phone_number : relatedUser?.phone_number,
    preferred_name: isNonEmptyString(contact?.preferred_name) ? contact?.preferred_name : relatedUser?.preferred_name,
    pronouns: isNonEmptyString(contact?.pronouns) ? contact?.pronouns : relatedUser?.pronouns,
    status: contact?.status,
    title: isNonEmptyString(contact?.title) ? contact?.title : relatedUser?.title,
    work_number: contact?.work_number,
  };

  const business_and_tax_details = pick(participant.business_and_tax, [
    "abn_acn",
    "company_trading_name",
    "is_aus_resident",
    "legal_entity_name",
    "residency_citizenship",
  ]);

  const account_details = {
    ...pick(participant.account, [
      "address",
      "amp_type",
      "is_indigenous",
      "socials",
      "status",
      "website",
      "year_of_birth",
      "year_of_death",
    ]),
    address: {
      ...pick(participant.account?.address, [
        "address_line_1",
        "address_line_2",
        "address_line_3",
        "city",
        "state",
        "postcode",
        "country",
      ]),
    },
  };

  const finance_contact_details = pick(participant.finance_contact, [
    "finance_email",
    "finance_phone",
    "preferred_name",
    "pronouns",
  ]);

  return {
    user_role: [
      ...(participant.participant_is_recipient ? [Role.ROYALTY_RECIPIENT] : []),
      ...(participant.participant_is_reporter ? [Role.RESALE_REPORTER] : []),
    ],
    participant_type: (participant.participant_types as ParticipantTypes[]) || [],
    user_details: {
      contact_details: removeKeysWhoseValuesAreNull(contact_details),
      tax_details: removeKeysWhoseValuesAreNull(business_and_tax_details),
      account_details: removeKeysWhoseValuesAreNull(account_details),
      bank_details: {} as BankDetails,
      finance_contact: removeKeysWhoseValuesAreNull(finance_contact_details),
    } as UserDetailsInput,
  };
};

export const RegisterSubmission = ({
  path,
}: {
  path: typeof ROUTES.Dashboard.NewAccounts | typeof ROUTES.RegisterSubmission;
}) => {
  const { auth } = useAuthenticationContext();

  const participantId = auth?.currentUserData?.participant?.participant_id;

  const { data } = useGetAccountDetailsByParticipantIdQuery({
    variables: { participantId: participantId ?? -1 },
    skip: !participantId,
  });

  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  const initialMultiFormData =
    data && auth && !isInternalUserCreatingAccount ? getFormDataForPrecreatedUser(data) : EMPTY_USER_INPUT;

  return (
    <>
      <DashboardHeader showResaleCart={false} />
      <div className={styles["content"]}>
        <Row className="justify-content-center">
          <Col xs={12} sm={12} md={10} lg={8} className="py-5 my-3">
            <MultiStepForm
              baseRoute={path.path}
              steps={steps(path, data !== undefined && !isInternalUserCreatingAccount)}
              initialMultiFormData={initialMultiFormData}
              key={initialMultiFormData.user_details.contact_details.first_name}
            />
          </Col>
        </Row>
      </div>
    </>
  );
};
